import { ReactNode } from 'react'
import { cn } from '@/lib/utils'

interface ItemCardProps {
  title: string
  description?: string
  imageUrl?: string
  imagePlaceholder?: string
  badge?: ReactNode
  metadata?: ReactNode
  footer?: ReactNode
  onClick?: () => void
  className?: string
  children?: ReactNode
  ariaLabel?: string
}

export function ItemCard({
  title,
  description,
  imageUrl,
  imagePlaceholder = '🥋',
  badge,
  metadata,
  footer,
  onClick,
  className,
  children,
  ariaLabel,
}: ItemCardProps) {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (onClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault()
      onClick()
    }
  }

  return (
    <div
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={onClick ? ariaLabel || title : undefined}
      className={cn(
        'group overflow-hidden rounded-2xl border border-[#e5e7eb] bg-white transition-all',
        onClick && 'cursor-pointer hover:shadow-md',
        className
      )}
    >
      {/* Image Section */}
      {(imageUrl || imagePlaceholder) && (
        <div className="relative aspect-video w-full overflow-hidden bg-gray-100">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={title}
              className="h-full w-full object-cover transition-transform group-hover:scale-105"
              onError={e => {
                e.currentTarget.src = '/move-placeholder.png'
              }}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-6xl">
              {imagePlaceholder}
            </div>
          )}
          {badge && <div className="absolute right-2 top-2">{badge}</div>}
        </div>
      )}

      {/* Content Section */}
      <div className="p-4">
        <h3 className="mb-1 line-clamp-1 font-semibold text-black">{title}</h3>
        {description && (
          <p className="mb-2 line-clamp-2 text-sm text-[#666666]">
            {description}
          </p>
        )}
        {metadata && (
          <div className="mt-2 text-sm text-[#666666]">{metadata}</div>
        )}
        {children}
      </div>

      {/* Footer Section */}
      {footer && (
        <div className="border-t border-gray-100 px-4 py-3">{footer}</div>
      )}
    </div>
  )
}
